import React, { useState } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import MapProduct from "../../utils/ProductMapper/MapProduct";
import ShowTime from "../../utils/ShowTime/ShowTime";

// Build the dropdown label for a license, mirroring what the old <Form.Select> showed.
export const licenseToOption = (l) => ({
  value: l.id,
  label: `${l.license_reference_id}${l.product ? ` - ${MapProduct(l.product, "text")}` : ""}${
    l.expires_at ? ` - expires ${ShowTime(l.expires_at)}` : ""
  }`,
  license: l,
});

/**
 * Searchable, paginated license picker (same pattern as UserSelector).
 * Loads lazily — the first page is only fetched when the menu first opens,
 * never on mount — and pages in 20 at a time as the user scrolls.
 *
 * Props:
 *   value     — the selected license object (or null). With isMulti, an ARRAY
 *               of license objects (an empty array when nothing is selected).
 *   onChange  — called with the full license object (or null when cleared).
 *               With isMulti, called with the array of license objects.
 *   isMulti   — allow several licenses to be picked at once. Renewals use this:
 *               one request can cover any number of the caller's licenses.
 */
export default function LicenseSelector({
  value,
  onChange,
  isMulti = false,
  placeholder = "Search your licenses by reference id...",
}) {
  const [options, setOptions] = useState([]); // current menu options (also serves as defaultOptions)
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const fetchLicenses = async (search = "", pageNum = 1) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/license/index`, {
        params: { limit: 20, page: pageNum, search },
        headers: { "access-token": localStorage.getItem("accessToken") },
      });
      if (response.data?.status) {
        const opts = (response.data.licenses || []).map(licenseToOption);
        setHasMore(opts.length > 0);
        return opts;
      }
      return [];
    } catch (error) {
      // Non-fatal — the selector simply shows no options.
      return [];
    }
  };

  // Lazy first load: only when the user actually opens the menu.
  const handleMenuOpen = async () => {
    if (options.length === 0) {
      const opts = await fetchLicenses("", 1);
      setPage(1);
      setOptions(opts);
    }
  };

  const loadOptions = async (inputValue, callback) => {
    setSearchTerm(inputValue);
    setPage(1);
    const opts = await fetchLicenses(inputValue, 1);
    setOptions(opts);
    callback(opts);
  };

  const loadMoreOnScroll = async () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    const more = await fetchLicenses(searchTerm, nextPage);
    if (more.length === 0) {
      setHasMore(false);
    } else {
      setOptions((prev) => [...prev, ...more]);
    }
  };

  // react-select hands back an array under isMulti and a single option (or
  // null) otherwise; normalise both to the shape this component promises.
  const selectedValue = isMulti
    ? (Array.isArray(value) ? value : []).map(licenseToOption)
    : (value ? licenseToOption(value) : null);

  const handleChange = (selected) => {
    if (isMulti) {
      onChange?.((selected || []).map((option) => option.license));
      return;
    }
    onChange?.(selected ? selected.license : null);
  };

  return (
    <AsyncSelect
      cacheOptions
      isSearchable
      isClearable
      isMulti={isMulti}
      // Without this the menu closes after every pick, which makes selecting
      // five licenses five round trips through the dropdown.
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={isMulti}
      value={selectedValue}
      defaultOptions={options}
      loadOptions={loadOptions}
      onMenuOpen={handleMenuOpen}
      onMenuScrollToBottom={loadMoreOnScroll}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
}
